import { useCallback, useRef } from "react";
import { toast } from "sonner";

import { useAuthContext } from "../auth/AuthContext";
import { refreshToLatest, setUpdateAvailable } from "./updateAvailable";
import { useVersionCheck } from "./useVersionCheck";

/**
 * Watches `/version.json` for a newer deploy and, when one lands, prompts the
 * user to refresh. Same behavior standalone and inside the extension iframe:
 * NEVER reloads on its own, since the user may be mid-flow.
 *
 *  - A persistent, dismissible toast with a Refresh action.
 *  - The pending version goes into the shared store so the header "Update" pill
 *    and the account-menu item keep the prompt reachable after a dismiss.
 *
 * Refresh navigates to `?_v=<latest>` (see `refreshToLatest`): an iframe can't
 * be hard-refreshed by the user, and the shell can't `reload()` a cross-origin
 * frame, so the app navigates its own window.
 *
 * Renders nothing. Mount once inside a tree that has <Toaster /> mounted.
 */
export default function VersionWatcher(): null {
  // Signed-in screens show the header "Update" pill; the login screen has no
  // header, so only point at the pill when it will actually be there.
  const { isAuthenticated } = useAuthContext();
  const authedRef = useRef(isAuthenticated);
  authedRef.current = isAuthenticated;

  const handleNewVersion = useCallback((latest: string) => {
    setUpdateAvailable(latest);
    toast("A new version of Atlas Copilot is available", {
      description: authedRef.current
        ? "Save or copy anything you're working on, then refresh here or with Update at the top. Until you do, some features may not work as expected."
        : "Save or copy anything you're working on, then refresh. Until you do, some features may not work as expected.",
      duration: Infinity,
      // The standalone <Toaster /> has no global close button, and a sticky
      // toast must be dismissible; the header pill brings the prompt back.
      closeButton: true,
      action: {
        label: "Refresh",
        onClick: () => refreshToLatest(latest),
      },
    });
  }, []);

  useVersionCheck(handleNewVersion);
  return null;
}
